import React, {useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";

import "./under-construction-scene";
import musicUrl from "./under-construction-assets/music.mp3";
import StripedProgressBar from "./components/striped-progress-bar";
import Countdown from "./components/count-down";
import {useTranslate} from "./useTranslate";
import "./under-construction.css";

const scene = window.SOURCE_SCENE;
const FRAME_DURATION = 1000 / scene.frameRate;
const sceneOffsets = {mc1: 10, mc2: -24, mc3: -36, mc4: -52};

const triangleOutlines = {
    sign1: {extraRingIndex: 28},
    sign2Base: {extraRingIndex: 26},
    sign3: {extraRingIndex: 26},
    sign4: {extraRingIndex: 26},
    signBig: {extraRingIndex: 26},
};

const rootStyle = {
    position: "relative",
    width: "100%",
    height: "100%",
    minHeight: "300px",
    overflow: "hidden",
    background: `
        radial-gradient(ellipse at center, rgba(255,255,255,0) 44%, rgba(172,186,193,.42) 100%),
        linear-gradient(180deg, #f9fafb 0%, #e4eaef 43.137%, #dae3e9 56.47%, #fff 66.274%, #dae7f1 100%)
    `,
};

const stageStyle = {
    position: "absolute",
    inset: 0,
    display: "block",
    width: "100%",
    height: "100%",
    overflow: "hidden",
};

function matrixValue(matrix) {
    return `matrix(${matrix.join(" ")})`;
}

function SvgPath({source, pathRef}) {
    return (
        <path
            ref={pathRef}
            d={source.d}
            fill={source.fill}
            stroke={source.stroke}
            strokeWidth={source.strokeWidth}
            opacity={source.opacity}
            transform={matrixValue(source.matrix)}
            fillRule="evenodd"
            strokeLinecap={source.stroke ? "round" : undefined}
            strokeLinejoin={source.stroke ? "round" : undefined}
        />
    );
}

function SvgPaths({paths, outlineName, pathRefs}) {
    const outline = triangleOutlines[outlineName];
    return paths.map((source, index) => {
        if (outline?.extraRingIndex === index) return null;

        return (
            <SvgPath
                key={`${outlineName || "path"}-${index}`}
                source={source}
                pathRef={pathRefs ? (element) => {
                    pathRefs.current[index] = element;
                } : undefined}
            />
        );
    });
}

function interpolateMatrix(from, to, amount) {
    return from.map((value, index) => value + (to[index] - value) * amount);
}

function cubicBezierAt(progress, [x1, y1, x2, y2]) {
    const sample = (time, first, second) => {
        const inverse = 1 - time;
        return 3 * inverse * inverse * time * first
            + 3 * inverse * time * time * second
            + time * time * time;
    };

    let low = 0;
    let high = 1;
    let time = progress;
    for (let iteration = 0; iteration < 12; iteration += 1) {
        time = (low + high) / 2;
        if (sample(time, x1, x2) < progress) low = time;
        else high = time;
    }
    return sample(time, y1, y2);
}

function headMatrixAt(frame) {
    const timeline = scene.sittingHeadTimeline;
    const frames = timeline.keyframes;
    let currentIndex = frames.findIndex(
        (entry) => frame >= entry.index && frame < entry.index + entry.duration,
    );
    if (currentIndex === -1) currentIndex = frames.length - 1;

    const current = frames[currentIndex];
    const next = frames[(currentIndex + 1) % frames.length];
    const progress = Math.min(1, Math.max(0, (frame - current.index) / current.duration));
    return interpolateMatrix(current.matrix, next.matrix, cubicBezierAt(progress, current.ease));
}

function shadowMatrixAt(frame) {
    const frames = scene.dancerShadowFrames;
    let currentIndex = frames.findIndex(
        (entry) => frame >= entry.index && frame < entry.index + entry.duration,
    );
    if (currentIndex === -1) currentIndex = frames.length - 1;

    const current = frames[currentIndex];
    const next = frames[(currentIndex + 1) % frames.length];
    const progress = Math.min(1, Math.max(0, (frame - current.index) / current.duration));
    return interpolateMatrix(current.matrix, next.matrix, progress);
}

function updateShapePaths(elements, framePaths) {
    if (!framePaths) return;
    elements.current.forEach((element, index) => {
        if (element && framePaths[index]) element.setAttribute("d", framePaths[index].d);
    });
}

function UnderConstruction() {
    const rootRef = useRef(null);
    const infoPanelRef = useRef(null);
    const audioRef = useRef(null);
    const soundRef = useRef(null);
    const dancerRef = useRef(null);
    const shadowMotionRef = useRef(null);
    const headMotionRef = useRef(null);
    const handsRefs = useRef([]);
    const legRefs = useRef([]);
    const mc1Ref = useRef(null);
    const mc2Ref = useRef(null);
    const mc3Ref = useRef(null);
    const mc4Ref = useRef(null);
    const mc5Ref = useRef(null);
    const [muted, setMuted] = useState(false);
    const [countDownFinished, setCountDownFinished] = useState(false);
    const {config} = useSelector((state) => state.app);
    const translate = useTranslate();

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;
        audio.muted = muted;
        if (!muted) audio.play().catch(() => {});
    }, [muted]);

    useEffect(() => {
        const root = rootRef.current;
        if (!root) return undefined;

        const camera = {
            width: 0,
            height: 0,
            mouseX: 0,
            positions: {},
            targets: {},
            panelX: 0,
            panelY: 0,
            panelTargetX: 0,
            panelTargetY: 0,
        };

        const applyPanelPerspective = () => {
            const shiftX = camera.panelX * 2.25;
            const shiftY = camera.panelY * -1.7;
            infoPanelRef.current?.style.setProperty(
                "transform",
                `translate3d(calc(-50% + ${shiftX}px), calc(-50% + ${shiftY}px), 0) perspective(720px) rotateX(${camera.panelY}deg) rotateY(${camera.panelX}deg)`,
            );
        };

        const applyClipPositions = () => {
            const {height} = camera;
            mc1Ref.current?.setAttribute("transform", `translate(${camera.positions.mc1} ${height / 2 - 60})`);
            mc2Ref.current?.setAttribute("transform", `translate(${camera.positions.mc2} ${height / 2 - 100})`);
            mc3Ref.current?.setAttribute("transform", `translate(${camera.positions.mc3} ${height / 2 - 110})`);
            mc4Ref.current?.setAttribute("transform", `translate(${camera.positions.mc4} ${height / 2 - 120})`);
            mc5Ref.current?.setAttribute("transform", `translate(${camera.positions.mc5} ${height})`);
        };

        const calculateTargets = () => {
            const width = camera.width;
            const mouseX = camera.mouseX;
            camera.targets.mc1 = width / 2 - (mouseX - width / 2) / 8 + sceneOffsets.mc1;
            camera.targets.mc2 = width / 2 - (mouseX - width / 2) / 50 + sceneOffsets.mc2;
            camera.targets.mc3 = width / 2 - (-mouseX - width / 2) / 50 + sceneOffsets.mc3;
            camera.targets.mc4 = width / 2 - (-mouseX - width / 2) / 20 + sceneOffsets.mc4;
            camera.targets.mc5 = width / 2 - (mouseX - width / 2) / 1.5;
        };

        const resize = () => {
            const {width, height} = root.getBoundingClientRect();
            camera.width = width;
            camera.height = height;
            camera.mouseX = width / 2;
            camera.positions = {
                mc1: width / 2 + sceneOffsets.mc1,
                mc2: width / 2 + sceneOffsets.mc2,
                mc3: width / 2 + sceneOffsets.mc3,
                mc4: width / 2 + sceneOffsets.mc4,
                mc5: width / 2 - 200,
            };
            camera.targets = {...camera.positions};
            camera.panelX = 0;
            camera.panelY = 0;
            camera.panelTargetX = 0;
            camera.panelTargetY = 0;
            applyClipPositions();
            applyPanelPerspective();
            calculateTargets();

            soundRef.current?.setAttribute(
                "transform",
                `translate(${width - 49.55} 50.45) scale(2.006073)`,
            );
        };

        const handlePointerMove = (event) => {
            const bounds = root.getBoundingClientRect();
            camera.mouseX = event.clientX - bounds.left;
            const normalizedX = (camera.mouseX / Math.max(1, bounds.width) - 0.5) * 2;
            const normalizedY = ((event.clientY - bounds.top) / Math.max(1, bounds.height) - 0.5) * 2;
            camera.panelTargetX = normalizedX * 15;
            camera.panelTargetY = normalizedY * -9;
            calculateTargets();
            const audio = audioRef.current;
            if (audio?.paused && !audio.muted) audio.play().catch(() => {});
        };

        const handlePointerLeave = () => {
            camera.mouseX = camera.width / 2;
            camera.panelTargetX = 0;
            camera.panelTargetY = 0;
            calculateTargets();
        };

        const resizeObserver = new ResizeObserver(resize);
        resizeObserver.observe(root);
        root.addEventListener("pointermove", handlePointerMove);
        root.addEventListener("pointerleave", handlePointerLeave);
        resize();

        let animationFrame;
        let previousDancerFrame = -1;
        let previousTime = performance.now();
        const startedAt = previousTime;

        const animate = (now) => {
            const elapsed = Math.max(0, now - startedAt);
            const absoluteFrame = Math.floor(elapsed / FRAME_DURATION);
            const dancerFrame = absoluteFrame % scene.dancerFrameCount;

            if (dancerFrame !== previousDancerFrame) {
                dancerRef.current?.setAttribute("d", scene.dancerFrames[dancerFrame]);
                shadowMotionRef.current?.setAttribute(
                    "transform",
                    matrixValue(shadowMatrixAt(dancerFrame)),
                );
                previousDancerFrame = dancerFrame;
            }

            const headFrame = absoluteFrame % scene.sittingHeadTimeline.frameCount;
            const handsFrame = absoluteFrame % scene.sittingHandsFrames.length;
            const legFrame = absoluteFrame % scene.sittingLegFrames.length;
            headMotionRef.current?.setAttribute("transform", matrixValue(headMatrixAt(headFrame)));
            updateShapePaths(handsRefs, scene.sittingHandsFrames[handsFrame]);
            updateShapePaths(legRefs, scene.sittingLegFrames[legFrame]);

            const delta = Math.min(100, now - previousTime);
            const smoothing = 1 - Math.pow(14 / 15, delta / FRAME_DURATION);
            Object.keys(camera.positions).forEach((name) => {
                camera.positions[name] += (camera.targets[name] - camera.positions[name]) * smoothing;
            });
            camera.panelX += (camera.panelTargetX - camera.panelX) * smoothing;
            camera.panelY += (camera.panelTargetY - camera.panelY) * smoothing;
            applyClipPositions();
            applyPanelPerspective();
            previousTime = now;
            animationFrame = requestAnimationFrame(animate);
        };

        animationFrame = requestAnimationFrame(animate);
        return () => {
            cancelAnimationFrame(animationFrame);
            resizeObserver.disconnect();
            root.removeEventListener("pointermove", handlePointerMove);
            root.removeEventListener("pointerleave", handlePointerLeave);
        };
    }, []);

    const toggleSound = () => setMuted((value) => !value);
    const handleSoundKeyDown = (event) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            toggleSound();
        }
    };

    return (
        <main ref={rootRef} className="uc-scene" style={rootStyle} aria-label="Under construction">
            <div className="uc-warning-bar uc-warning-bar--top" aria-hidden="true">
                <StripedProgressBar value={100} height={42} striped animated />
            </div>
            <svg xmlns="http://www.w3.org/2000/svg" style={stageStyle}>
                <defs>
                    <radialGradient id="source-shadow">
                        <stop offset="0" stopColor="#212938" />
                        <stop offset="1" stopColor="#364552" stopOpacity="0" />
                    </radialGradient>
                    <radialGradient id="pale-shadow">
                        <stop offset="44.3%" stopColor="#fff" stopOpacity="0" />
                        <stop offset="100%" stopColor="#acbac1" />
                    </radialGradient>
                    <linearGradient id="source-background" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0" stopColor="#f9fafb" />
                        <stop offset="43.137%" stopColor="#e4eaef" />
                        <stop offset="56.47%" stopColor="#dae3e9" />
                        <stop offset="66.274%" stopColor="#fff" />
                        <stop offset="100%" stopColor="#dae7f1" />
                    </linearGradient>
                    <filter id="distant-blur" x="-35%" y="-35%" width="170%" height="170%">
                        <feGaussianBlur stdDeviation="1.15" />
                    </filter>
                    <filter id="far-blur" x="-30%" y="-30%" width="160%" height="160%">
                        <feGaussianBlur stdDeviation="0.55" />
                    </filter>
                </defs>

                <g ref={mc4Ref} filter="url(#distant-blur)" opacity=".68">
                    <SvgPaths paths={scene.symbols.sign4} outlineName="sign4" />
                </g>
                <g ref={mc3Ref} filter="url(#far-blur)" opacity=".84">
                    <SvgPaths paths={scene.symbols.sign3} outlineName="sign3" />
                </g>
                <g ref={mc2Ref}>
                    <SvgPaths paths={scene.symbols.sign2Base} outlineName="sign2Base" />
                    <g transform="translate(18.4 11.65)">
                        <SvgPaths paths={scene.symbols.sittingTorso} />
                        <g transform="translate(2.3 -19.35)">
                            <g ref={headMotionRef}><SvgPaths paths={scene.symbols.sittingHead} /></g>
                        </g>
                        <g transform="translate(0 .55)">
                            <SvgPaths paths={scene.sittingHandsFrames[0]} pathRefs={handsRefs} />
                        </g>
                        <g transform="translate(4.75 14.85)">
                            <SvgPaths paths={scene.sittingLegFrames[0]} pathRefs={legRefs} />
                        </g>
                    </g>
                </g>
                <g ref={mc1Ref}>
                    <g transform="translate(-287.75 -14.05)">
                        <SvgPaths paths={scene.symbols.sign1} outlineName="sign1" />
                    </g>
                    <g transform="translate(-144.25 -81.25)">
                        <g ref={shadowMotionRef}><SvgPaths paths={scene.symbols.dancerShadow} /></g>
                        <path
                            ref={dancerRef}
                            d={scene.dancerFrames[0]}
                            fill="#000"
                            fillRule="evenodd"
                        />
                    </g>
                </g>
                <g ref={mc5Ref}><SvgPaths paths={scene.symbols.signBig} outlineName="signBig" /></g>

                <g
                    ref={soundRef}
                    role="button"
                    tabIndex="0"
                    aria-label={muted ? "Turn sound on" : "Mute sound"}
                    onClick={toggleSound}
                    onKeyDown={handleSoundKeyDown}
                    style={{cursor: "pointer", outline: "none"}}
                >
                    <SvgPaths paths={muted ? scene.symbols.soundOff : scene.symbols.soundOn} />
                </g>
            </svg>

            <section ref={infoPanelRef} className={`uc-depth-panel ${config?.dir || ""}`}>
                <img
                    className="uc-zofino-logo"
                    src="/assets/brand/zofino-mark-512.png"
                    alt="Zofino"
                />
                <div className="uc-countdown-card">
                    <div className="uc-countdown-title">{translate("under-construction")}</div>
                    <div className="uc-countdown-value" dir="ltr">
                        {countDownFinished
                            ? translate("count-down-finished")
                            : (
                                <Countdown
                                    targetDate={config?.["under-construction"]}
                                    format="datetime"
                                    onComplete={() => setCountDownFinished(true)}
                                />
                            )}
                    </div>
                </div>
            </section>

            <div className="uc-warning-bar uc-warning-bar--bottom" aria-hidden="true">
                <StripedProgressBar value={100} height={42} striped animated />
            </div>
            <audio ref={audioRef} src={musicUrl} loop preload="auto" />
        </main>
    );
}

export default UnderConstruction;
